import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { PurposeEnum } from 'src/app/models/shared-models';
import {
  FeatureFlags,
  FeatureFlagService,
} from 'src/app/services/feature-flag.service';
import { SessionService } from 'src/app/services/session.service';
import { environment } from 'src/environments/environment';
import {
  IGeneralInformationModel,
  IGetLotOnPlanPurposeModel,
  IPurposeModel,
} from '../models/veg-wizard-models';
import { Logger } from './logger-service';

@Injectable({
  providedIn: 'root',
})
export class PurposeService {
  constructor(
    protected http: HttpClient,
    private logger: Logger,
    private sessionService: SessionService,
    private featureFlagService: FeatureFlagService
  ) {}

  setPurpose$(
    purposeCode: string,
    sessionId: string | undefined
  ): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.apiUrl}/session/set-purpose`, {
        purposeCode: purposeCode,
        sessionKey: sessionId,
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.logger.error('setPurpose error', err);
          return of(false);
        }),
        map(() => true) // return true, as it should be HttpStatusCode OK
      );
  }

  doesNotificationExistForLotPlanAndPurpose$(
    model: IPurposeModel,
    sessionId: string | undefined
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${environment.apiUrl}/session/does-notification-exist-for-lotPlan-and-purpose`,
        { purposeCode: model.purposeCode, sessionKey: sessionId }
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.logger.error(
            'doesNotificationExistForLotPlanAndPurpose error',
            err
          );
          return of(false);
        }),
        map(() => true) // return true, as it should be HttpStatusCode OK
      );
  }

  /**
   * Returns available land use purposes based on lot plan selection.
   */
  getPurposes$(): Observable<IPurposeModel[]> {
    const sessionId = this.sessionService.getSessionIdOrError();

    const url =
      `${environment.apiUrl}/session/purposes-for-categories?sessionKey=` +
      encodeURIComponent(sessionId);
    return this.http.get<IGetLotOnPlanPurposeModel>(url).pipe(
      map((result) => {
        let purposes = [] as IPurposeModel[];
        if (!result) {
          return purposes;
        }

        purposes = result.purposes.sort(
          (a: IPurposeModel, b: IPurposeModel) => {
            if (a && b) {
              if (a.purposeDescription > b.purposeDescription) {
                return 1;
              }
              if (a.purposeDescription < b.purposeDescription) {
                return -1;
              }
            }
            return 0;
          }
        );

        return purposes;
      })
    );
  }

  getPurposeMessage(purposeCode: string): IGeneralInformationModel {
    const purposeQuestionaire: IGeneralInformationModel | null =
      {} as IGeneralInformationModel;

    switch (purposeCode) {
      case PurposeEnum.publicSafety:
        purposeQuestionaire.promptQuestionMessage =
          'Have you taken pre-clearing photographs and GPS coordinates of the location of the public safety risk?';
        purposeQuestionaire.userSelectsNoDisplayMessage =
          'You must identify the public safety risk by taking pre-clearing photographs and taking GPS coordinates of the' +
          ' location that the pre-clearing photographs were taken.';
        purposeQuestionaire.userSelectsYesDisplayMessage =
          'Please retain this information for your records.' +
          ' These records should be made available to the Department of Natural Resources and Mines, Manufacturing and Regional and Rural Development upon request.';
        break;
      case PurposeEnum.encroachment:
        purposeQuestionaire.promptQuestionMessage =
          'Can you demonstrate that encroachment has occurred on the property?';
        purposeQuestionaire.userSelectsNoDisplayMessage =
          'Before you can continue this notification you must demonstrate that encroachment has occurred.';
        purposeQuestionaire.userSelectsYesDisplayMessage =
          'Please retain evidence of encroachment for your records. These records should ' +
          'be made available to the Department of Natural Resources and Mines, Manufacturing and Regional and Rural Development upon request.';
        break;
      case PurposeEnum.managingRegrowthDensity:
        purposeQuestionaire.promptQuestionMessage =
          'Have you taken pre-clearing photographs and GPS coordinates of the area in which you intend to clear?';
        purposeQuestionaire.userSelectsNoDisplayMessage =
          'You must take photographs and GPS coordinates of the proposed clearing area.';
        purposeQuestionaire.userSelectsYesDisplayMessage =
          'Please retain this information for your records. These records should be made available to ' +
          'the Department of Natural Resources and Mines, Manufacturing and Regional and Rural Development upon request.';
        break;
      case PurposeEnum.nativeForestPractice:
        if (this.featureFlagService.featureEnabled(FeatureFlags.NFP2019)) {
          purposeQuestionaire.promptQuestionMessage =
            'Will clearing occur in an area of essential habitat for TBC?';
          purposeQuestionaire.userSelectsNoDisplayMessage =
            'Clearing in an area of essential habitat is prohibited under the code.';
          purposeQuestionaire.userSelectsYesDisplayMessage =
            'Valid to go to next screen';
          purposeQuestionaire.secondPromptQuestionMessage =
            'Will the clearing under this notification be part of an ongoing forestry business?';
          purposeQuestionaire.secondUserSelectsNoDisplayMessage =
            'Clearing under this code must be part of an ongoing forestry business.';
          purposeQuestionaire.secondUserSelectsYesDisplayMessage =
            'Valid to go to next screen second';
        }

        break;
      case PurposeEnum.certifiedNativeForestPractice:
        if (this.featureFlagService.featureEnabled(FeatureFlags.NFP2019)) {
          purposeQuestionaire.promptQuestionMessage =
            'Will clearing occur in an area of essential habitat for TBC?';
          purposeQuestionaire.userSelectsNoDisplayMessage =
            'Clearing in an area of essential habitat is prohibited under the code.';
          purposeQuestionaire.userSelectsYesDisplayMessage =
            'Valid to go to next screen';
        }
        break;
    }

    return purposeQuestionaire;
  }
}
